import React, { useEffect, useState } from "react";

import { useForm } from "../useForm";

import Input from "../../controls/Input";
import SelectMenu from "../../controls/SelectMenu";
import TraceableLotNumber from "../../controls/TraceableLotNumber";
import ReadonlyIdentifierField from "../../controls/ReadonlyIdentifierField";
import {
  classNames,
  generateReceivingLotNumber,
  standardWeightOptions,
} from "../../../shared/utility";
import ButtonSpinner from "../../controls/ButtonSpinner";
import DollarInputText from "../../controls/inputs/DollarInputText";

const initialFValues = {
  net_weight: 0,
  tare_weight: 0,
  weight_type: { value: -1 },
  carton_count: 0,
  unit_cost: 0,
  piece_count: 0,
  total_cost: 0,
};

export default function AddItemToReceivingForm(props) {
  const {
    addItemToReceiving,
    isEdit,
    product,
    supplierNumber,
    getWarehouses,
    getWarehouseLocations,
  } = props;

  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const [warehouseLocationsOptions, setWarehouseLocationsOptions] = useState(
    []
  );

  const validate = (fieldValues = values) => {
    let temp = { ...errors };

    if ("lot_number" in fieldValues) {
      temp.lot_number = null;
      if (fieldValues.lot_number === "") {
        temp.lot_number = "This field is required.";
      }
    }

    if ("net_weight" in fieldValues) {
      temp.net_weight = null;
      if (fieldValues.net_weight === "" || fieldValues.net_weight === 0) {
        temp.net_weight = "This field is required.";
      }
    }

    if ("tare_weight" in fieldValues) {
      temp.tare_weight = null;
      if (fieldValues.tare_weight === "") {
        temp.tare_weight = "This field is required.";
      }
    }

    if ("weight_type" in fieldValues) {
      temp.weight_type = null;
      if (fieldValues.weight_type) {
        if (fieldValues.weight_type.value === -1) {
          temp.weight_type = "This field is required.";
        }
      } else {
        temp.weight_type = "This field is required.";
      }
    }

    if ("carton_count" in fieldValues) {
      temp.carton_count = null;
      if (fieldValues.carton_count === "" || fieldValues.carton_count === 0) {
        temp.carton_count = "This field is required.";
      }
    }

    if ("unit_cost" in fieldValues) {
      temp.unit_cost = null;
      if (fieldValues.unit_cost === "" || fieldValues.unit_cost === 0) {
        temp.unit_cost = "This field is required.";
      }
    }

    if ("warehouse_id" in fieldValues) {
      temp.warehouse_id = null;
      if (fieldValues.warehouse_id) {
        if (fieldValues.warehouse_id.value === -1) {
          temp.warehouse_id = "This field is required.";
        }
      } else {
        temp.warehouse_id = "This field is required.";
      }
    }

    if ("warehouse_location_id" in fieldValues) {
      temp.warehouse_location_id = null;
      if (fieldValues.warehouse_location_id) {
        if (fieldValues.warehouse_location_id.value === -1) {
          temp.warehouse_location_id = "This field is required.";
        }
      } else {
        temp.warehouse_location_id = "This field is required.";
      }
    }

    setErrors({
      ...temp,
    });

    return Object.values(temp).every((x) => x === null);
  };

  const {
    values,
    setValues,
    errors,
    setErrors,
    handleInputChange,
    handleMultipleInputChange,
  } = useForm(initialFValues, true, validate);

  function getSelectedWarehouse(warehouse_id, warehouses = warehouseOptions) {
    var warehouseSelected = {};
    if (product) {
      if (product.warehouse_id) {
        warehouseSelected = warehouses.find((el) => el.value === warehouse_id);

        if (typeof warehouseSelected === "undefined") {
          warehouseSelected = { value: null };
        }
      }
    }
    return warehouseSelected;
  }

  function getSelectedWarehouseLocation(
    warehouse_location_id,
    wLocations = warehouseLocationsOptions
  ) {
    var warehouseLocationSelected = {};
    if (product) {
      if (product.warehouse_location_id) {
        warehouseLocationSelected = wLocations.find(
          (el) => el.value === warehouse_location_id
        );

        if (typeof warehouseLocationSelected === "undefined") {
          warehouseLocationSelected = { value: null };
        }
      }
    }
    return warehouseLocationSelected;
  }

  const onLoadData = async function () {
    const warehousesResult = await getWarehouses();
    if (warehousesResult.success) {
      setWarehouseOptions(warehousesResult.data);
    }
    const warehouseLocationsReslt = await getWarehouseLocations(
      product.warehouse_id
    );
    if (warehouseLocationsReslt.success) {
      setWarehouseLocationsOptions(warehouseLocationsReslt.data);
    }

    var warehouseSelected = getSelectedWarehouse(
      product.warehouse_id,
      warehousesResult.data
    );
    var warehouseLocationSelected = getSelectedWarehouseLocation(
      product.warehouse_location_id,
      warehouseLocationsReslt.data
    );

    var selectedWeightType = {};
    if (product) {
      if (product.stock_unit_of_measure) {
        if (product.stock_unit_of_measure === 2) {
          selectedWeightType = { value: 3, label: "pieces" };
        } else {
          selectedWeightType = standardWeightOptions.find(
            (el) => el.value === product.stock_unit_of_measure
          );
        }
      }
    }

    if (isEdit) {
      setValues({
        ...values,
        ...product,
        weight_type: selectedWeightType,
        warehouse_id: warehouseSelected,
        warehouse_location_id: warehouseLocationSelected,
      });
    } else {
      setValues({
        ...values,
        weight_type: selectedWeightType,
        product_id: product.id,
        product_name: product.name,
        plu: product.plu,
        unit_cost: product.unit_cost,
        warehouse_id: warehouseSelected,
        warehouse_location_id: warehouseLocationSelected,
        lot_number: generateReceivingLotNumber(supplierNumber, product.plu),
      });
    }
  };

  useEffect(() => {
    onLoadData();
  }, [product]);

  const [inProgress, setInProgress] = useState(false);

  function calculateTotalCost(amount, unit_cost, carton_count) {
    const total_cost = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount * unit_cost * carton_count);

    return total_cost;
  }

  const handleSubmit = async function () {
    setInProgress(true);
    if (validate()) {
      let result = await addItemToReceiving(values);
    }
    setInProgress(false);
  };

  const onLoadWarehouseLocations = async function (selectedWarehouse) {
    setValues({
      ...values,
      warehouse_id: selectedWarehouse,
      warehouse_location_id: null,
    });
    const result = await getWarehouseLocations(selectedWarehouse.value);
    if (result.success) {
      setWarehouseLocationsOptions(result.data);
    }
  };

  return (
    <form className="space-y-4">
      <div>
        <p className="text-md font-bold">{values.product_name}</p>
        <p className="text-md font-bold">{values.plu}</p>
      </div>
      <div className="grid grid-cols-3 gap-4">
        {isEdit ? (
          <ReadonlyIdentifierField label="Lot Number">
            <TraceableLotNumber lotNumber={values.lot_number} traceType="supplier" />
          </ReadonlyIdentifierField>
        ) : (
          <Input
            label="Lot Number"
            name="lot_number"
            onChange={handleInputChange}
            value={values.lot_number}
            labelOn={true}
            type="text"
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm w-48"
            error={errors.lot_number}
          />
        )}

        <div className="grid grid-cols-3 col-span-2 gap-4">
          <Input
            label={`Net Weight ${values.weight_type.value === 3 ? "(kg)" : ""}`}
            name="net_weight"
            onChange={(e) => {
              handleMultipleInputChange({
                net_weight: e.target.value,
                total_cost:
                  values.weight_type.value === 3
                    ? values.total_cost
                    : calculateTotalCost(
                        e.target.value,
                        values.unit_cost,
                        values.carton_count
                      ),
              });
            }}
            value={values.net_weight}
            labelOn={true}
            type="number"
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm w-48"
            error={errors.net_weight}
          />

          <Input
            label="Tare Weight"
            name="tare_weight"
            onChange={handleInputChange}
            value={values.tare_weight}
            labelOn={true}
            type="number"
            disabled={isEdit}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm w-48"
            error={errors.tare_weight}
          />

          <SelectMenu
            handleInputChange={(e) => {
              const chosenUnit = e.target.value.value;
              const newTotalCost = calculateTotalCost(
                chosenUnit === 3 ? values.piece_count : values.net_weight,
                values.unit_cost,
                values.carton_count
              );
              var newPieceCount = values.piece_count;
              if (chosenUnit !== 3) {
                newPieceCount = 0;
              }

              setValues({
                ...values,
                weight_type: e.target.value,
                piece_count: newPieceCount,
                total_cost: newTotalCost,
              });
            }}
            name="weight_type"
            disabled={product.stock_unit_of_measure === 2}
            options={standardWeightOptions}
            value={values.weight_type}
            title={"Weight Unit"}
            error={errors.weight_type}
            showLabel={true}
          />
        </div>
      </div>
      <div
        className={classNames(
          values.weight_type.value === 3
            ? "grid grid-cols-4 gap-4"
            : "grid grid-cols-3 gap-4"
        )}
      >
        <Input
          label="Carton Count"
          name="carton_count"
          onChange={(e) => {
            handleMultipleInputChange({
              carton_count: e.target.value,
              total_cost: calculateTotalCost(
                values.weight_type.value === 3
                  ? values.piece_count
                  : values.net_weight,
                values.unit_cost,
                e.target.value
              ),
            });
          }}
          value={values.carton_count}
          labelOn={true}
          type="number"
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm w-48"
          error={errors.carton_count}
        />

        <Input
          label="Piece Count"
          name="piece_count"
          onChange={(e) => {
            handleMultipleInputChange({
              piece_count: e.target.value,
              total_cost: calculateTotalCost(
                e.target.value,
                values.unit_cost,
                values.carton_count
              ),
            });
          }}
          value={values.piece_count}
          error={errors.piece_count}
          labelOn={true}
          type="number"
          parentClass={classNames(
            values.weight_type.value === 3 ? null : "hidden"
          )}
        />

        <DollarInputText
          label="Unit Cost"
          name="unit_cost"
          onChange={(e) => {
            handleMultipleInputChange({
              unit_cost: e.target.value,
              total_cost: calculateTotalCost(
                values.weight_type.value === 3
                  ? values.piece_count
                  : values.net_weight,
                e.target.value,
                values.carton_count
              ),
            });
          }}
          value={values.unit_cost}
          labelOn={true}
          type="number"
          className="mt-1 block w-full focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm w-48"
          error={errors.unit_cost}
        />

        <Input
          label="Total Cost"
          name="total_cost"
          value={values.total_cost}
          labelOn={true}
          type="text"
          disabled={true}
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm w-48"
          error={errors.total_cost}
        />
      </div>
      <div className="grid grid-cols-3 gap-4">
        <SelectMenu
          handleInputChange={(e) => onLoadWarehouseLocations(e.target.value)}
          name="warehouse_id"
          options={warehouseOptions}
          value={values.warehouse_id}
          title={"Warehouse"}
          error={errors.warehouse_id}
          showLabel={true}
          menuPortalTarget={document.body}
        />

        <SelectMenu
          handleInputChange={handleInputChange}
          name="warehouse_location_id"
          options={warehouseLocationsOptions}
          value={values.warehouse_location_id}
          title={"Location"}
          error={errors.warehouse_location_id}
          showLabel={true}
          menuPortalTarget={document.body}
        />
      </div>
      <button
        type="button"
        className="inline-flex justify-center rounded-md bg-main-purple px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
        onClick={handleSubmit}
      >
        <ButtonSpinner spin={inProgress} text={isEdit ? "Save" : "Add"} />
      </button>
    </form>
  );
}
